import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle2, ShieldAlert, ShieldOff, Check } from "lucide-react";
import { toast } from "sonner";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";

export default function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const readOne = async (id) => {
    try { await api.post(`/notifications/${id}/read`); load(); } catch {}
  };

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid="notifications-page-root">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <span className="chip-gold"><Bell size={14} /> Activity</span>
            <h1 className="mt-3 font-serif text-4xl tracking-tight">Notifications</h1>
            <p className="mt-2 text-muted-foreground">Everything worth knowing about your projects, applications and reviews.</p>
          </div>
          <button onClick={readAll} className="btn-ghost border" data-testid="notifications-read-all"><CheckCircle2 size={14} /> Mark all read</button>
        </div>
        <div className="mt-10 space-y-3">
          {loading && Array.from({ length: 3 }).map((_, i) => <div key={i} className="card-soft h-20 animate-pulse" />)}
          {!loading && items.length === 0 && (
            <div className="card-soft text-center py-16 sm:py-20">
              <div className="mx-auto w-16 h-16 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center">
                <Bell size={26} className="text-[hsl(var(--sb-forest-2))]" />
              </div>
              <h3 className="mt-5 font-serif text-2xl">You&rsquo;re all caught up.</h3>
              <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                Applications, messages and reviews will land here so you never miss a beat.
              </p>
              <Link to="/jobs" className="btn-primary inline-flex mt-6">Discover projects</Link>
            </div>
          )}
          {items.map((n) => {
            const style = kindStyle(n.kind);
            const Icon = style.icon;
            return (
              <Link
                key={n.id}
                to={n.link || "/notifications"}
                onClick={() => !n.read && readOne(n.id)}
                className={`card-soft block ${n.read ? "opacity-70" : ""} ${style.border}`}
                data-testid="notification-row"
                data-notification-kind={n.kind || ""}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    {Icon && (
                      <div className={`shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-full ${style.iconBg}`}>
                        <Icon size={16} className={style.iconColor} />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--sb-gold))] shadow-[0_0_0_3px_hsl(var(--sb-gold)/0.2)]" />}
                        <span className={`font-medium ${!n.read ? "text-foreground" : ""}`}>{n.title}</span>
                        {style.chip && (
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] ${style.chip}`}>
                            {style.chipLabel}
                          </span>
                        )}
                      </div>
                      {n.body && (
                        <p className="mt-1 text-sm text-muted-foreground">{n.body}</p>
                      )}
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </SiteShell>
  );
}

/**
 * Phase B.3 — distinct visual + verbal treatment for confidentiality
 * notifications so a reader can tell at a glance that:
 *   - "You've been accepted" is a celebration (forest / check).
 *   - "Confidentiality required" is an action gate (clay / shield-alert
 *     + the "Action required" chip + a destructive border).
 *   - "Confidentiality declined" (author-facing) is an informative
 *     cancellation (muted red / shield-off).
 */
function kindStyle(kind) {
  switch (kind) {
    case "confidentiality_required":
    case "confidentiality_catchup":
    case "confidentiality_updated":
      return {
        icon: ShieldAlert,
        iconBg: "bg-[hsl(var(--sb-clay))]/15",
        iconColor: "text-[hsl(var(--sb-clay-2))]",
        border: "border-l-4 border-l-[hsl(var(--sb-clay))]",
        chip: "border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/10 text-[hsl(var(--sb-clay-2))]",
        chipLabel: "Action required",
      };
    case "confidentiality_declined":
      return {
        icon: ShieldOff,
        iconBg: "bg-destructive/10",
        iconColor: "text-destructive",
        border: "border-l-4 border-l-destructive/60",
        chip: "border-destructive/40 bg-destructive/10 text-destructive",
        chipLabel: "Declined",
      };
    case "confidentiality_acknowledged":
    case "confidentiality_acknowledged_admin":
      return {
        icon: CheckCircle2,
        iconBg: "bg-[hsl(var(--sb-forest))]/15",
        iconColor: "text-[hsl(var(--sb-forest-2))]",
        border: "",
        chip: null,
      };
    case "application_accepted":
      return {
        icon: Check,
        iconBg: "bg-[hsl(var(--sb-forest))]/15",
        iconColor: "text-[hsl(var(--sb-forest-2))]",
        border: "border-l-4 border-l-[hsl(var(--sb-forest))]/40",
        chip: "border-[hsl(var(--sb-forest))]/40 bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))]",
        chipLabel: "Accepted",
      };
    default:
      return { icon: null, iconBg: "", iconColor: "", border: "", chip: null };
  }
}
